import { LOCAL_STORAGE_DEVICE_ID_KEY } from '@module/mdrt-org/shared/utils/data/constants/local-storage-keys';

const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replaceAll('-', '+').replaceAll('_', '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let index = 0; index < rawData.length; index++) {
    outputArray[index] = rawData.codePointAt(index) ?? 0;
  }

  return outputArray;
};

export const isPushSupported = (): boolean =>
  typeof globalThis !== 'undefined' &&
  'Notification' in globalThis &&
  'serviceWorker' in navigator &&
  'PushManager' in globalThis;

const SERVICE_WORKER_READY_TIMEOUT_MS = 10_000;

const waitForServiceWorkerReady = async (): Promise<ServiceWorkerRegistration> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_resolve, reject) => {
    timeoutId = setTimeout(
      () => reject(new Error('Service worker did not become ready')),
      SERVICE_WORKER_READY_TIMEOUT_MS
    );
  });

  try {
    return await Promise.race([navigator.serviceWorker.ready, timeout]);
  } finally {
    clearTimeout(timeoutId);
  }
};

export const createPushSubscription = async (): Promise<null | PushSubscription> => {
  if (Notification.permission === 'denied') return null;

  if (Notification.permission === 'default') {
    const result = await Notification.requestPermission();
    if (result !== 'granted') return null;
  }

  const vapidPublicKey = window.PUBLIC_ENV.vapidPublicKey;

  if (!vapidPublicKey) {
    throw new Error('VAPID public key is not configured');
  }

  const reg = await waitForServiceWorkerReady();
  return reg.pushManager.subscribe({
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey).buffer as ArrayBuffer,
    userVisibleOnly: true,
  });
};

export const getActivePushSubscription = async (): Promise<null | PushSubscription> => {
  const reg = await waitForServiceWorkerReady();
  return reg.pushManager.getSubscription();
};

export const extractSubscriptionKeys = (subscription: PushSubscription) => {
  const rawP256dh = subscription.getKey('p256dh');
  const rawAuth = subscription.getKey('auth');

  if (!rawP256dh || !rawAuth) {
    throw new Error('PushSubscription is missing required keys');
  }

  const p256dh = btoa(String.fromCodePoint(...new Uint8Array(rawP256dh)));
  const auth = btoa(String.fromCodePoint(...new Uint8Array(rawAuth)));

  return { auth, endpoint: subscription.endpoint, p256dh };
};

// Assembles the backend payload from a browser PushSubscription: reuses the persisted
// device id (or mints and stores a new one) and extracts the subscription keys. Single
// source of truth for the shape posted via postWebPushSubscription.
export const buildWebPushSubscriptionPayload = (
  subscription: PushSubscription,
  { accessToken, personId }: { accessToken: string | undefined; personId: number | undefined }
) => {
  const existingDeviceId = localStorage.getItem(LOCAL_STORAGE_DEVICE_ID_KEY);
  const deviceId = existingDeviceId ?? crypto.randomUUID();

  if (!existingDeviceId) {
    localStorage.setItem(LOCAL_STORAGE_DEVICE_ID_KEY, deviceId);
  }

  const { auth, endpoint, p256dh } = extractSubscriptionKeys(subscription);

  return {
    accessToken,
    auth,
    deviceId,
    endpoint,
    expirationTime: subscription.expirationTime,
    p256dh,
    personId,
    userAgent: navigator.userAgent,
  };
};
